import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { UpdateUserData, User } from '../types/user';
import { authService } from '../services/authService';
import { tokenManager } from '../services/api/tokenManager';
import { useErrorHandler } from '@hooks';
import { STORAGE_KEYS } from '../constants/api';
import { useNavigate, useLocation } from 'react-router-dom';

interface AuthState {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUserProfile: (data: UpdateUserData) => Promise<void>;
  clearError: () => void;
  error: { message: string; type: 'error' | 'warning' | 'info'; code?: string } | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { error, handleError, clearError, showSuccess } = useErrorHandler();
  const isInitialized = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLoggedOut = useCallback(() => {
    try {
      tokenManager.clearTokens();
      setUser(null);
      
      // Si estamos en la página de reserva, no redirigir al login porque el formulario de reserva
      // incluye su propio flujo de login/registro.
      try {
        const pathname = location.pathname;
        const isReservationPath = pathname.includes('/reserve') || pathname.includes('/reservation');
        if (!isReservationPath) {
          navigate('/login', { replace: true });
        }
      } catch {
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('Error handling logout:', error);
    }
  }, [navigate, location]);

  const initializeAuth = useCallback(async (isCancelled: boolean) => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    try {
      if (tokenManager.hasValidTokens()) {
        const savedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA);

        if (savedUserData && !isCancelled) {
          try {
            const currentUser = await authService.getCurrentUser();
            setUser(currentUser);
          } catch (err) {
            console.warn('Error obteniendo:', err);
            if (!isCancelled) setUser(null);
          }
        }
      } else {
        if (!isCancelled) setUser(null);
      }
    } finally {
      if (!isCancelled) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isCancelled = false;

    window.addEventListener("auth:logged-out", handleLoggedOut);

    initializeAuth(isCancelled);

    return () => {
      isCancelled = true;
      window.removeEventListener("auth:logged-out", handleLoggedOut);
    };
  }, [handleLoggedOut, initializeAuth]);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const response = await authService.login({ email, password });

      setUser(response.user);
      showSuccess('¡Inicio de sesión exitoso!');
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (name: string, email: string, password: string) => {
    setLoading(true);
    try {
      const response = await authService.signup({ name, email, password });

      setUser(response.user);
      showSuccess('¡Cuenta creada exitosamente!');
    } catch (error) {
      handleError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      showSuccess('Sesión cerrada correctamente');
    } catch (error) {
      console.error('Error durante logout:', error);
      setUser(null);
      tokenManager.clearTokens();
    }
  };

  const updateUserProfile = async (data: Partial<User>) => {
    if (!user) {
      throw new Error('Usuario no autenticado');
    }
    setLoading(true);
    try {
      const updatedUser = await authService.updateUserProfile(data);
      setUser(updatedUser);
      showSuccess('Perfil actualizado correctamente');
    } catch (error) {
      console.error('Error actualizando perfil:', error);
      handleError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    updateUserProfile,
    error,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};